/**
 * Stable analytics taxonomy for the APT Conversion Program.
 * Shared modules should build event payloads here and let renderer/server
 * transports decide how those payloads are emitted.
 */

export const CONVERSION_REGISTER_CATEGORY = 'conversion_register';
export const CONVERSION_ALERTS_CATEGORY = 'conversion_alerts';
export const CONVERSION_PROVIDER_CATEGORY = 'conversion_provider';
export const CONVERSION_OFFICER_CATEGORY = 'conversion_officer';
export const CONVERSION_EMPLOYER_CATEGORY = 'conversion_employer';
export const CONVERSION_PROFESSIONAL_CATEGORY = 'conversion_professional';
export const CONVERSION_AUTHOR_CATEGORY = 'conversion_author';

export const CONVERSION_CATEGORIES = [
  CONVERSION_REGISTER_CATEGORY,
  CONVERSION_ALERTS_CATEGORY,
  CONVERSION_PROVIDER_CATEGORY,
  CONVERSION_OFFICER_CATEGORY,
  CONVERSION_EMPLOYER_CATEGORY,
  CONVERSION_PROFESSIONAL_CATEGORY,
  CONVERSION_AUTHOR_CATEGORY,
] as const;

export type ConversionCategory = (typeof CONVERSION_CATEGORIES)[number];

export const EVENT_DETAIL_VIEW_ACTION = 'event_detail_view';
export const EVENT_REGISTER_CTA_CLICK_ACTION = 'event_register_cta_click';
export const EXPRESS_REGISTER_START_ACTION = 'express_register_start';
export const EXPRESS_REGISTER_COMPLETE_ACTION = 'express_register_complete';
export const EXPRESS_REGISTER_FAIL_ACTION = 'express_register_fail';
export const EXTERNAL_REGISTER_CLICK_ACTION = 'external_register_click';
export const REGISTER_ACCOUNT_UPGRADE_CLICK_ACTION =
  'register_account_upgrade_click';
export const EXPRESS_REGISTER_CLAIMED_ACTION = 'express_register_claimed';
export const FIRST_WIN_PROMPT_SHOWN_ACTION = 'first_win_prompt_shown';
export const FIRST_WIN_PRIMARY_CLICK_ACTION = 'first_win_primary_click';
export const FIRST_WIN_REMINDER_SENT_ACTION = 'first_win_reminder_sent';

export const GEO_ALERT_CTA_CLICK_ACTION = 'geo_alert_cta_click';
export const GEO_ALERT_GUEST_SUBMIT_ACTION = 'geo_alert_guest_submit';
export const GEO_ALERT_GUEST_CONFIRMED_ACTION = 'geo_alert_guest_confirmed';
export const GEO_ALERT_GUEST_UNSUB_ACTION = 'geo_alert_guest_unsub';
export const GEO_ALERT_GUEST_MERGED_ACTION = 'geo_alert_guest_merged';
export const GEO_ALERT_AUTH_SAVE_ACTION = 'geo_alert_auth_save';

export const HOST_CTA_CLICK_ACTION = 'host_cta_click';
export const INSTRUCTOR_CTA_CLICK_ACTION = 'instructor_cta_click';
export const PROVIDER_APPLY_LITE_SUBMIT_ACTION = 'provider_apply_lite_submit';
export const PROVIDER_PROFILE_COMPLETE_ACTION = 'provider_profile_complete';
export const PROVIDER_FIRST_EVENT_PUBLISHED_ACTION =
  'provider_first_event_published';

export const FOR_OFFICERS_VIEW_ACTION = 'for_officers_view';
export const FOR_OFFICERS_CTA_CLICK_ACTION = 'for_officers_cta_click';
export const FOR_EMPLOYERS_VIEW_ACTION = 'for_employers_view';
export const FOR_EMPLOYERS_CTA_CLICK_ACTION = 'for_employers_cta_click';
export const FOR_PROFESSIONALS_VIEW_ACTION = 'for_professionals_view';
export const FOR_PROFESSIONALS_CTA_CLICK_ACTION = 'for_professionals_cta_click';
export const WRITE_FOR_APT_VIEW_ACTION = 'write_for_apt_view';
export const WRITE_FOR_APT_CTA_CLICK_ACTION = 'write_for_apt_cta_click';

export const REGISTER_CONVERSION_ACTIONS = [
  EVENT_DETAIL_VIEW_ACTION,
  EVENT_REGISTER_CTA_CLICK_ACTION,
  EXPRESS_REGISTER_START_ACTION,
  EXPRESS_REGISTER_COMPLETE_ACTION,
  EXPRESS_REGISTER_FAIL_ACTION,
  EXTERNAL_REGISTER_CLICK_ACTION,
  REGISTER_ACCOUNT_UPGRADE_CLICK_ACTION,
  EXPRESS_REGISTER_CLAIMED_ACTION,
  FIRST_WIN_PROMPT_SHOWN_ACTION,
  FIRST_WIN_PRIMARY_CLICK_ACTION,
  FIRST_WIN_REMINDER_SENT_ACTION,
] as const;

export const ALERTS_CONVERSION_ACTIONS = [
  GEO_ALERT_CTA_CLICK_ACTION,
  GEO_ALERT_GUEST_SUBMIT_ACTION,
  GEO_ALERT_GUEST_CONFIRMED_ACTION,
  GEO_ALERT_GUEST_UNSUB_ACTION,
  GEO_ALERT_GUEST_MERGED_ACTION,
  GEO_ALERT_AUTH_SAVE_ACTION,
] as const;

export const PROVIDER_CONVERSION_ACTIONS = [
  HOST_CTA_CLICK_ACTION,
  INSTRUCTOR_CTA_CLICK_ACTION,
  PROVIDER_APPLY_LITE_SUBMIT_ACTION,
  PROVIDER_PROFILE_COMPLETE_ACTION,
  PROVIDER_FIRST_EVENT_PUBLISHED_ACTION,
] as const;

export const OFFICER_CONVERSION_ACTIONS = [
  FOR_OFFICERS_VIEW_ACTION,
  FOR_OFFICERS_CTA_CLICK_ACTION,
] as const;

export const EMPLOYER_CONVERSION_ACTIONS = [
  FOR_EMPLOYERS_VIEW_ACTION,
  FOR_EMPLOYERS_CTA_CLICK_ACTION,
] as const;

export const PROFESSIONAL_CONVERSION_ACTIONS = [
  FOR_PROFESSIONALS_VIEW_ACTION,
  FOR_PROFESSIONALS_CTA_CLICK_ACTION,
] as const;

export const AUTHOR_CONVERSION_ACTIONS = [
  WRITE_FOR_APT_VIEW_ACTION,
  WRITE_FOR_APT_CTA_CLICK_ACTION,
] as const;

export type RegisterConversionAction =
  (typeof REGISTER_CONVERSION_ACTIONS)[number];
export type AlertsConversionAction = (typeof ALERTS_CONVERSION_ACTIONS)[number];
export type ProviderConversionAction =
  (typeof PROVIDER_CONVERSION_ACTIONS)[number];
export type OfficerConversionAction =
  (typeof OFFICER_CONVERSION_ACTIONS)[number];
export type EmployerConversionAction =
  (typeof EMPLOYER_CONVERSION_ACTIONS)[number];
export type ProfessionalConversionAction =
  (typeof PROFESSIONAL_CONVERSION_ACTIONS)[number];
export type AuthorConversionAction = (typeof AUTHOR_CONVERSION_ACTIONS)[number];
export type ConversionAction =
  | RegisterConversionAction
  | AlertsConversionAction
  | ProviderConversionAction
  | OfficerConversionAction
  | EmployerConversionAction
  | ProfessionalConversionAction
  | AuthorConversionAction;

export type ConversionAnalyticsCustomParameters = Record<string, any>;

export interface ConversionAnalyticsEvent {
  action: ConversionAction;
  category: ConversionCategory;
  label?: string;
  custom_parameters?: ConversionAnalyticsCustomParameters;
}

/**
 * Shared builder for renderer/server consumers that need an AnalyticsEvent-
 * shaped payload without taking a runtime dependency on renderer tracking.
 */
export function buildConversionAnalyticsEvent(
  input: ConversionAnalyticsEvent,
): ConversionAnalyticsEvent {
  return {
    action: input.action,
    category: input.category,
    ...(input.label ? { label: input.label } : {}),
    ...(input.custom_parameters
      ? { custom_parameters: input.custom_parameters }
      : {}),
  };
}
